import React, { useState, useRef } from 'react';
import { 
  WhatsappShareButton, WhatsappIcon,
  FacebookMessengerShareButton, FacebookMessengerIcon,
  EmailShareButton, EmailIcon,
  WeiboShareButton, WeiboIcon
} from 'react-share';
import { Copy, Check } from 'lucide-react';
import Modal from '../Modal/Modal';
import { useMetadata } from '../../context/MetadataContext';
import './ShareModal.css';

/**
 * ShareModal component for sharing challenge/puzzle links via various platforms
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @param {string} props.shareUrl - The URL to share
 * @param {string} props.modalType - Type of share modal ('challenge', 'share', or 'invite')
 */
const ShareModal = ({ isOpen, onClose, shareUrl, modalType = 'challenge' }) => {
  const { getText } = useMetadata();
  const [copied, setCopied] = useState(false);
  const urlInputRef = useRef(null);

  // Share message for social platforms
  const getShareMessage = () => {
    if (modalType === 'invite') return getText('tokens.invite_share_message', 'Join me on GridRank!');
    if (modalType === 'challenge') return getText('duel_mode.challenged');
    return getText('create.share_message', 'Check out this puzzle I created!');
  };

  const getMessageTitle = () => {
    if (modalType === 'invite') return getText('tokens.invite_share_title', 'Join GridRank!');
    if (modalType === 'challenge') return getText('create.challenge_title');
    return getText('create.share_title', 'Try this puzzle!');
  };

  const shareMessage = getShareMessage();
  const messageTitle = getMessageTitle();
  // Handle copy to clipboard with fallback method
  const handleCopyLink = () => {
    if (urlInputRef.current) {
      urlInputRef.current.select();
      
      // Try using the clipboard API first
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(shareUrl)
            .then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            })
            .catch(err => {
              console.error('Failed to copy text: ', err);
              // Fallback to execCommand
              fallbackCopyToClipboard();
            });
        } else {
          // Fallback for browsers without clipboard API
          fallbackCopyToClipboard();
        }
      } catch (error) {
        console.error('Copy failed:', error);
        fallbackCopyToClipboard();
      }
    }
  };
  
  // Fallback copy method using document.execCommand
  const fallbackCopyToClipboard = () => {
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        console.error('Fallback copy failed');
      }
    } catch (err) {
      console.error('Fallback copy error:', err);
    }
  };

  const getModalTitle = () => {
    if (modalType === 'invite') return getText('tokens.invite_title', 'Invite Friends');
    if (modalType === 'challenge') return getText('actions.action_challenge');
    return getText('create.share_puzzle');
  };

  const getModalDescription = () => {
    if (modalType === 'invite') return getText('tokens.invite_share_tooltip', 'Share your invite link with friends!');
    if (modalType === 'challenge') return getText('actions.challenge_tooltip');
    return getText('create.share_tooltip', 'Share this puzzle with friends!');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getModalTitle()}
    >
      <div className="shareModalContent">
        <p className="shareDescription">
          {getModalDescription()}
        </p>
        
        <div className="urlContainer">
          <input
            ref={urlInputRef}
            type="text"
            value={shareUrl}
            readOnly
            className="shareUrlInput"
            aria-label="Challenge URL"
          />
          <button 
            onClick={handleCopyLink} 
            className="copyButton"
            aria-label="Copy link"
          >
            {copied ? <Check /> : <Copy />}
          </button>
        </div>
        
        <div className="shareButtonsContainer">
          <div className="shareOption">
            <WhatsappShareButton url={shareUrl} title={shareMessage}>
              <WhatsappIcon size={48} round />
            </WhatsappShareButton>
          </div>
          
          <div className="shareOption">
            <FacebookMessengerShareButton url={shareUrl} appId="YOUR_FACEBOOK_APP_ID">
              <FacebookMessengerIcon size={48} round />
            </FacebookMessengerShareButton>
          </div>
          
          <div className="shareOption">
            <EmailShareButton url={shareUrl} subject={messageTitle} body={shareMessage}>
              <EmailIcon size={48} round />
            </EmailShareButton>
          </div>
          
          <div className="shareOption">
            <WeiboShareButton url={shareUrl} title={shareMessage}>
              <WeiboIcon size={48} round />
            </WeiboShareButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ShareModal;
